import React from 'react';

export interface FormattedConjugationProps {
  accompli?: string;
  inaccompli?: string;
  className?: string;
}

/**
 * Parses raw conjugation string into 1ère personne du singulier and 3ème personne du singulier.
 * Formats supported:
 * 1. "1re pers | 3e pers" (with pipe)
 * 2. "3e pers / 1re pers" (legacy standard with slash, e.g. "Yečča (Il a mangé) / Ččiɣ (J'ai mangé)")
 * 3. Any single string fallback
 */
export function parseConjugationPersons(
  rawText: string | undefined,
  type: 'accompli' | 'inaccompli'
): { firstPerson: string; thirdPerson: string } | null {
  if (!rawText || !rawText.trim()) return null;
  const trimmed = rawText.trim();

  // If already separated by pipe '|'
  if (trimmed.includes('|')) {
    const parts = trimmed.split('|').map((s) => s.trim());
    return {
      firstPerson: parts[0] || '',
      thirdPerson: parts[1] || '',
    };
  }

  // If separated by slash '/'
  if (trimmed.includes('/')) {
    const parts = trimmed.split('/').map((s) => s.trim());
    const partA = parts[0] || '';
    const partB = parts[1] || '';

    // Check which one is 1st person (starts with J' / Je, ends with -eɣ / -iɣ)
    const isFirstPerson = (s: string) =>
      /\b(j'|je)\b/i.test(s) ||
      /\b\w+([eé]ɣ|iɣ)\b/i.test(s) ||
      /\b(tteččeɣ|ttesseg|gganeɣ|tekkireɣ|ttruḥuɣ|ttruɣ|urareɣ)\b/i.test(s);

    const isThirdPerson = (s: string) =>
      /\b(il|elle)\b/i.test(s) ||
      /^[YyIt][a-z]/i.test(s) ||
      /\b(ad\s+[ydt])/i.test(s);

    if (isFirstPerson(partA) && !isFirstPerson(partB)) {
      return { firstPerson: partA, thirdPerson: partB };
    }
    if (isFirstPerson(partB) && !isFirstPerson(partA)) {
      return { firstPerson: partB, thirdPerson: partA };
    }

    // Default legacy convention: partA was 3rd person ("Yečča..."), partB was 1st person ("Ččiɣ...")
    if (type === 'accompli') {
      return { firstPerson: partB, thirdPerson: partA };
    } else {
      // Inaccompli legacy: partA was 3rd person ("Itečč..."), partB was 3rd futur ("Ad yečč...")
      // If partB doesn't look like 1st pers, we present both neatly
      return { firstPerson: partB, thirdPerson: partA };
    }
  }

  // Single string fallback
  return { firstPerson: trimmed, thirdPerson: '' };
}

/**
 * Uniform Conjugation Box component.
 * Displays:
 * Inaccompli : 1ère personne sing | 3ème personne sing
 * Accompli : 1ère personne sing | 3ème personne sing
 */
export const ConjugationBox: React.FC<FormattedConjugationProps> = ({
  accompli,
  inaccompli,
  className = '',
}) => {
  const parsedInacc = parseConjugationPersons(inaccompli, 'inaccompli');
  const parsedAcc = parseConjugationPersons(accompli, 'accompli');

  if (!parsedInacc && !parsedAcc) return null;

  return (
    <div className={`flex flex-col gap-1.5 text-xs border-t border-amber-900/10 pt-2 mt-1 ${className}`}>
      {/* Ligne Inaccompli */}
      {parsedInacc && (
        <div className="flex flex-wrap items-center gap-1.5 bg-blue-50/90 border border-blue-200/90 rounded-lg px-2.5 py-1 text-stone-900 shadow-2xs">
          <span className="text-[11px] font-black text-blue-800 uppercase tracking-wider shrink-0">
            🔄 Inaccompli :
          </span>
          <div className="inline-flex flex-wrap items-center gap-1.5 font-semibold text-xs leading-tight">
            <span className="text-blue-950 font-bold">{parsedInacc.firstPerson}</span>
            {parsedInacc.thirdPerson && (
              <>
                <span className="text-blue-400 font-normal">|</span>
                <span className="text-stone-800 font-medium">{parsedInacc.thirdPerson}</span>
              </>
            )}
          </div>
        </div>
      )}

      {/* Ligne Accompli */}
      {parsedAcc && (
        <div className="flex flex-wrap items-center gap-1.5 bg-amber-50/90 border border-amber-300/80 rounded-lg px-2.5 py-1 text-stone-900 shadow-2xs">
          <span className="text-[11px] font-black text-amber-800 uppercase tracking-wider shrink-0">
            ⏳ Accompli :
          </span>
          <div className="inline-flex flex-wrap items-center gap-1.5 font-semibold text-xs leading-tight">
            <span className="text-amber-950 font-bold">{parsedAcc.firstPerson}</span>
            {parsedAcc.thirdPerson && (
              <>
                <span className="text-amber-400 font-normal">|</span>
                <span className="text-stone-800 font-medium">{parsedAcc.thirdPerson}</span>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
